<template>
  <AppTooltip :disabled="!iconOnly" location="right">
    <template #activator="{ props }">
      <div
        class="app-sidebar-item"
        :class="{
          'app-sidebar-item--nav': item.kind === 'nav',
          'app-sidebar-item--folder': item.kind === 'folder',
          'app-sidebar-item--section': item.kind === 'section',
          'app-sidebar-item--active': item.active,
          'app-sidebar-item--icon-only': iconOnly,
          'app-sidebar-item--clickable': isClickable,
        }"
        :data-locator="item.dataLocator"
        :style="computedStyle"
        :role="isClickable ? 'button' : undefined"
        :tabindex="isClickable ? 0 : undefined"
        :aria-current="item.active ? 'page' : undefined"
        v-bind="props"
        @click="onClick"
        @keydown.enter.prevent="onClick"
        @keydown.space.prevent="onClick"
      >
        <app-material-icon
          :name="resolvedIconName"
          :filled="isFilledIcon"
          :weight="resolvedIconWeight"
          :color="resolvedIconColor"
          :type="resolvedIconType"
          class="app-sidebar-item__icon"
          :class="{
            'app-sidebar-item__icon--nav': item.kind === 'nav' || item.kind === 'section',
            'app-sidebar-item__icon--folder': item.kind === 'folder',
          }"
        />

        <div v-if="!iconOnly" class="app-sidebar-item__label">
          <span>{{ item.label }}</span>
          <AppFeatureTag v-if="item.tag" :tag="item.tag" size="sm" />
        </div>
      </div>
    </template>
    <span>{{ item.label }}</span>
  </AppTooltip>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'

import type { AppSidebarItemData } from '@/common/app-sidebar/appSidebar.types'
import AppFeatureTag from '@/common/AppFeatureTag.vue'
import AppTooltip from '@/common/AppTooltip.vue'

export default defineComponent({
  name: 'AppSidebarItem',
  components: { AppFeatureTag, AppTooltip },
  props: {
    item: {
      type: Object as PropType<AppSidebarItemData>,
      required: true,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  inheritAttrs: false,
  emits: ['select'],
  setup (props, { emit }) {
    const isClickable = computed(() => props.item.kind !== 'section')
    const iconNameMap: Record<string, string> = {
      archive: 'archive',
      'box-archive': 'inventory_2',
      'credit-card': 'credit_card',
      envelope: 'mail',
      'fa-envelope': 'mail',
      'fa-gear': 'settings',
      'fa-id-card': 'badge',
      'fa-key': 'key',
      'fa-user': 'person',
      folder: 'folder',
      'folder-open': 'folder_open',
      gear: 'settings',
      house: 'home',
      'home-storage': 'home_storage',
      home_storage: 'home_storage',
      'id-card': 'badge',
      key: 'key',
      'rectangle-history': 'history',
      schedule: 'schedule',
      user: 'person',
      users: 'group',
    }

    const resolvedIconType = computed(() => props.item.iconType ?? 'material')
    const resolvedIconColor = computed(() => (
      props.item.iconColor || '#375576'
    ))
    const isFilledIcon = computed(() => (
      props.item.kind === 'folder' && resolvedIconType.value === 'material'
    ))
    const resolvedIconName = computed(() => {
      if (resolvedIconType.value === 'fa') {
        return props.item.icon
      }

      return iconNameMap[props.item.icon] ?? props.item.icon.replace(/-/g, '_')
    })
    const resolvedIconWeight = computed(() => (
      resolvedIconType.value === 'fa'
        ? (props.item.iconWeight || 'far')
        : 400
    ))

    const computedStyle = computed(() => {
      if (props.iconOnly) {
        return undefined
      }

      if (props.item.kind !== 'folder') {
        return undefined
      }

      const depth = props.item.depth ?? 0

      return {
        paddingLeft: `${8 + depth * 8}px`,
      }
    })

    function onClick () {
      if (!isClickable.value) {
        return
      }

      emit('select', props.item.key)
    }

    return {
      computedStyle,
      isFilledIcon,
      isClickable,
      onClick,
      resolvedIconName,
      resolvedIconColor,
      resolvedIconType,
      resolvedIconWeight,
    }
  },
})
</script>

<style scoped lang="scss">
.app-sidebar-item {
  display: flex;
  align-items: center;
  width: 224px;
  max-width: 100%;
  height: 36px;
  padding: 8px;
  border-radius: 4px;
  gap: 8px;
  opacity: 1;
  color: #103051;
  box-sizing: border-box;

  &--clickable {
    cursor: pointer;
  }

  &--nav:not(.app-sidebar-item--active):hover,
  &--folder:not(.app-sidebar-item--active):hover {
    background: #00172E0D;
  }

  &--active {
    background: #EDF0F2;
  }

  &--section {
    background: transparent;
  }

  &--folder:not(.app-sidebar-item--active) {
    background: transparent;
  }

  &--icon-only {
    width: 36px;
    min-width: 36px;
  }

  &__icon {
    flex: 0 0 auto;

    &--nav {
      color: #3c5d86;
    }

    &--folder {
      color: #178EE3;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    color: #103051;
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0;
    overflow: hidden;
    white-space: nowrap;

    span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
